import React, { useState, useId, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import Button from 'reactstrap/lib/Button';
import { scroller } from 'react-scroll';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { Collapse } from 'site-modules/shared/components/collapse/collapse';

import './vdp-module.scss';

/**
 * VdpModule - A flexible container component for Vehicle Detail Page (VDP) sections
 *
 * Provides a consistent layout structure with optional features like:
 * - Shadow styling and custom indentation
 * - Section titles with configurable heading tags
 * - Collapsible content with expand/collapse functionality
 * - Smooth scrolling behavior when collapsing
 * - Gradient overlay for collapsed content
 * - Accessibility support with proper ARIA attributes
 *
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Content to render inside the module
 * @param {boolean} props.withShadow - Apply shadow styling to the module
 * @param {string} props.className - Additional CSS classes
 * @param {ReactNode} props.sectionTitle - Title text or element for the module
 * @param {Object} props.collapseConfig - Configuration for collapsible behavior
 * @param {string} props.collapseConfig.minHeight - Minimum height before collapse is enabled
 * @param {number} props.collapseConfig.scrollOffset - Scroll offset when collapsing
 * @param {string} props.collapseConfig.btnTrackingId - Tracking ID for collapse button
 */
export function VdpModule({
  children,
  withShadow = false,
  className = '',
  withIndents = true,
  titleClassName = 'heading-3',
  headerTag: Heading = 'h2',
  sectionTitle = null,
  indentationSize = 1,
  subCopy = null,
  subCopyClassName = 'mb-1',
  collapseConfig = null,
  contentCollapseClasses = '',
  collapseGradientClassName = '',
  ...restModuleParams
}) {
  const collapseId = `collapse${useId()}`;
  const [isOpen, setIsOpen] = useState(false);
  const [isCollapsible, setIsCollapsible] = useState(!!collapseConfig);
  const moduleRef = useRef();

  function toggle() {
    if (isOpen && collapseConfig) {
      scroller.scrollTo(collapseId, {
        offset: collapseConfig.scrollOffset,
        smooth: 'easeInOutQuad',
        isDynamic: true,
      });
    }

    setIsOpen(!isOpen);
  }

  const module = (
    <div
      className={classnames('vdp-module', className, {
        'vdp-module-shadow': withShadow,
        [`p-${indentationSize}`.replace('.', '_')]: withIndents,
        [contentCollapseClasses]: collapseConfig && !isCollapsible,
      })}
      ref={moduleRef}
      {...restModuleParams}
    >
      {sectionTitle && (
        <Heading className={classnames({ 'mb-1_5': !subCopy, 'mb-0_5': subCopy }, titleClassName)}>
          {sectionTitle}
        </Heading>
      )}
      {subCopy && <div className={subCopyClassName}>{subCopy}</div>}
      {children}
    </div>
  );
  const dataTrackingId = !!collapseConfig && collapseConfig.btnTrackingId;
  const dataTrackingValue = isOpen ? 'expand' : 'collapse';
  const minHeight = get(collapseConfig, 'minHeight');

  useEffect(() => {
    if (minHeight && moduleRef && moduleRef.current && moduleRef.current.offsetHeight < parseInt(minHeight, 10)) {
      setIsCollapsible(false);
    }
  }, [minHeight]);

  return isCollapsible ? (
    <div className={classnames('vdp-module-collapse content-collapse pos-r', contentCollapseClasses)}>
      <ScrollElement id={collapseId} />
      <Collapse
        isOpen={isOpen}
        collapseHeight={minHeight}
        addState
        transition="height 280ms cubic-bezier(0.4, 0, 0.2, 1)"
        className={classnames({ collapsing: !isOpen })}
      >
        {module}
      </Collapse>
      <button
        className={classnames('collapse-gradient pos-a w-100 bottom-0 border-0', collapseGradientClassName, {
          'd-none': isOpen,
        })}
        onClick={toggle}
        data-no-refresh="true"
        data-tracking-id={dataTrackingId}
        data-tracking-value={dataTrackingValue}
        tabIndex={-1}
        aria-hidden
      />
      <div className="collapse-control-calc d-flex justify-content-center align-items-center w-100 bottom-0 pos-r">
        <Button
          size="lg"
          color="link"
          className="pos-a p-0"
          onClick={toggle}
          data-no-refresh
          data-tracking-id={dataTrackingId}
          data-tracking-value={dataTrackingValue}
          aria-expanded={isOpen}
          aria-label={isOpen ? 'Collapse section' : 'Expand section'}
        >
          <span className="circle-btn rounded-circle d-flex justify-content-center align-items-center">
            <i
              className={classnames('text-blue-50 icon', {
                'icon-arrow-down3': !isOpen,
                'icon-arrow-up3': isOpen,
              })}
              aria-hidden
            />
          </span>
        </Button>
      </div>
    </div>
  ) : (
    module
  );
}

VdpModule.propTypes = {
  withShadow: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  sectionTitle: PropTypes.node,
  withIndents: PropTypes.bool,
  indentationSize: PropTypes.number,
  headerTag: PropTypes.string,
  titleClassName: PropTypes.string,
  subCopy: PropTypes.node,
  subCopyClassName: PropTypes.string,
  collapseConfig: PropTypes.shape({
    minHeight: PropTypes.string,
    scrollOffset: PropTypes.number,
    btnTrackingId: PropTypes.string,
  }),
  contentCollapseClasses: PropTypes.string,
  collapseGradientClassName: PropTypes.string,
};
